/**
 * Deterministic normalization helpers for listing state.
 *
 * Rules (from the frozen contract):
 *  - Unordered multi-select arrays are copied and sorted by stable scalar value;
 *    caller arrays are never mutated.
 *  - Scope is serialized to a fixed-field object for use in query-key factories.
 *  - Sort is serialized in fixed field order.
 *  - Filter values may be any JSON-serializable shape; generic helpers normalize
 *    individual array fields.  Page adapters call these helpers as needed.
 *
 * This module contains NO inspection-specific field names.
 */
// ---------------------------------------------------------------------------
// Multi-select normalization
// ---------------------------------------------------------------------------
/**
 * Returns a new array sorted by stable scalar comparison.  The original array
 * is never modified.  Handles `null` and `undefined` entries by sorting them
 * to the front so hashing is still deterministic.
 */
export function normalizeMultiSelect(values) {
    return values
        .map((value, index) => ({ index, sortKey: scalarSortKey(value), value }))
        .sort((left, right) => {
        if (left.sortKey === right.sortKey)
            return left.index - right.index;
        return left.sortKey < right.sortKey ? -1 : 1;
    })
        .map(({ value }) => value);
}
function scalarSortKey(value) {
    if (value === null)
        return '0:';
    if (value === undefined)
        return '1:';
    if (typeof value === 'number')
        return `2:${String(value)}`;
    return `3:${value}`;
}
// ---------------------------------------------------------------------------
// Scope serialization
// ---------------------------------------------------------------------------
/**
 * Returns a plain, fixed-field object safe for JSON.stringify in a query key.
 * All fields are present in every variant so key hashing is deterministic
 * regardless of scope type.
 */
export function serializeScope(scope) {
    const base = {
        type: scope.type,
        id: 'id' in scope ? scope.id : null
    };
    return base;
}
// ---------------------------------------------------------------------------
// Sort serialization
// ---------------------------------------------------------------------------
/**
 * Returns a fixed-field sort object (field before direction) or null.
 * Ensures query-key hashing is independent of object construction order.
 */
export function serializeSort(sort) {
    if (!sort)
        return null;
    return { field: sort.field, direction: sort.direction };
}
// ---------------------------------------------------------------------------
// Query-key input builder
// ---------------------------------------------------------------------------
/**
 * Produces the deterministic portion of a query key that captures all
 * parameters affecting fetch identity.  Page adapters prepend a feature
 * namespace to this tuple.
 *
 * Field order is fixed: scope → normalizedFilters → sort → page.number → page.size.
 */
export function buildQueryKeySegment(scope, normalizedFilters, sort, pageNumber, pageSize) {
    return [serializeScope(scope), normalizedFilters, serializeSort(sort), pageNumber, pageSize];
}
